import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6flgjghk82orf0cl20';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string | null)?.trim() ?? '';
    if (honeypot) {
      setStatus('success');
      setMessage('感谢你的反馈，我们已经收到啦！');
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = typeof value === 'string' ? value.trim() : '';
      if (str) params.append(key, str);
    });

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('提交成功，感谢你的反馈与建议！');
        form.reset();
      } else {
        setStatus('error');
        setMessage(isSpam ? '提交内容未通过校验，请调整后重试。' : serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试。');
    }
  };

  const inputClass =
    'w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors';

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-1.5">
            你的昵称
          </label>
          <input id="fb-name" name="name" type="text" required placeholder="请输入昵称" className={inputClass} />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱地址
          </label>
          <input id="fb-email" name="email" type="email" required placeholder="you@example.com" className={inputClass} />
        </div>
      </div>

      <div>
        <label htmlFor="fb-topic" className="block text-sm text-foreground-700 mb-1.5">
          反馈类型
        </label>
        <select id="fb-topic" name="topic" defaultValue="内容建议" className={`${inputClass} pr-8`}>
          <option value="内容建议">内容建议</option>
          <option value="播放问题">播放问题</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他问题">其他问题</option>
        </select>
      </div>

      <div>
        <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-1.5">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请描述你的问题或建议（最多 500 字）"
          className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors resize-none"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-anti-field"
      />

      <div className="flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="h-11 px-7 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'submitting' ? '提交中…' : '提交反馈'}
        </button>
        {message && (
          <span
            className={`text-sm ${
              status === 'success' ? 'text-accent-400' : 'text-primary-400'
            }`}
          >
            <i className={`${status === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mr-1 align-middle`}></i>
            {message}
          </span>
        )}
      </div>
    </form>
  );
}